import type { MscKeyConfig, StorageEnvelope, StorageType } from '../types';
import { STORAGE_TYPES } from '../types';
import {
  cookieRemoveByKey,
  cookieStorageExists,
  cookieStorageGet,
  cookieStorageRemove,
  cookieStorageSet,
  parseCookieNames,
} from './cookieStorage';
import {
  webStorageHasValue,
  webStorageReadEnvelopeWithFlag,
  webStorageRemove,
  webStorageSet,
} from './webStorage';

const isCookieStorage = (cfg: MscKeyConfig): boolean => cfg.storage === STORAGE_TYPES.COOKIES;

export function storageSet(cfg: MscKeyConfig, value: unknown, expireAtEpochMs: number): boolean {
  return isCookieStorage(cfg)
    ? cookieStorageSet(cfg, value, expireAtEpochMs)
    : webStorageSet(cfg, value, expireAtEpochMs);
}

export function storageRemove(cfg: MscKeyConfig): void {
  if (isCookieStorage(cfg)) {
    cookieStorageRemove(cfg);
  } else {
    webStorageRemove(cfg);
  }
}

export function storageHasValue(cfg: MscKeyConfig): boolean {
  return isCookieStorage(cfg) ? cookieStorageExists(cfg) : webStorageHasValue(cfg);
}

export function storageReadEnvelopeWithFlag(cfg: MscKeyConfig): {
  exists: boolean;
  envelope: StorageEnvelope | null;
  raw: string | null;
} {
  if (isCookieStorage(cfg)) {
    return { exists: cookieStorageExists(cfg), envelope: null, raw: null };
  }
  return webStorageReadEnvelopeWithFlag(cfg);
}

export function cookieStorageGetValue(cfg: MscKeyConfig): unknown {
  if (!isCookieStorage(cfg)) {
    throw new Error('cookieStorageGetValue can only be used with cookies storage');
  }
  return cookieStorageGet(cfg);
}

export function getStorageKeys(storageType: StorageType): string[] {
  try {
    if (storageType === STORAGE_TYPES.LOCAL) return Object.keys(localStorage);
    if (storageType === STORAGE_TYPES.SESSION) return Object.keys(sessionStorage);
    if (storageType === STORAGE_TYPES.COOKIES) return parseCookieNames();
    return [];
  } catch {
    return [];
  }
}

export function getMatchingKeys(pattern: string, storageType: StorageType): string[] {
  try {
    const regex = new RegExp(pattern);
    return getStorageKeys(storageType).filter((key) => regex.test(key));
  } catch {
    return [];
  }
}

export function removeKeyFromStorage(key: string, storageType: StorageType): void {
  try {
    if (storageType === STORAGE_TYPES.LOCAL) {
      localStorage.removeItem(key);
    } else if (storageType === STORAGE_TYPES.SESSION) {
      sessionStorage.removeItem(key);
    } else if (storageType === STORAGE_TYPES.COOKIES) {
      cookieRemoveByKey(key);
    }
  } catch {
    console.log('error removing key from storage', key, storageType);
  }
}

export { cookieStorageExists };
